/**
 * iter71 · Safety net around PdfActivityViewer.
 *
 * Any uncaught render/effect error inside the viewer used to blank the
 * whole page (React 18 default behaviour). We wrap the tree in a class
 * component with `componentDidCatch` so the user sees a controlled
 * fallback and the console still gets the stack trace for debugging.
 *
 * Kept intentionally minimal — no telemetry, no retry loops — because
 * we do NOT want any regression in the viewer's happy path.
 */
import React from 'react';

export class PdfActivityErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[PdfActivityErrorBoundary] uncaught render error:', error, info?.componentStack);
  }

  handleReset = () => {
    this.setState({ error: null });
    if (typeof this.props.onReset === 'function') this.props.onReset();
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        role="alert"
        data-testid="pdf-viewer-error-boundary"
        style={{
          padding: '24px 20px',
          margin: '24px auto',
          maxWidth: 560,
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: 10,
          color: '#7f1d1d',
          fontSize: 14,
          lineHeight: 1.5,
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>
          No pudimos abrir el visor de la actividad
        </div>
        <div style={{ marginBottom: 10 }}>
          Se produjo un error inesperado al cargar el PDF. Puedes intentarlo de nuevo
          o cerrar el diálogo. Si el problema persiste, avisa al docente.
        </div>
        {this.state.error?.message && (
          <div
            style={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 12,
              background: '#fff1f2',
              padding: '6px 10px',
              borderRadius: 6,
              marginBottom: 10,
              wordBreak: 'break-word',
            }}
          >
            {String(this.state.error.message)}
          </div>
        )}
        <button
          type="button"
          data-testid="pdf-viewer-error-retry"
          onClick={this.handleReset}
          style={{
            background: '#b91c1c',
            color: '#fff',
            border: 'none',
            padding: '8px 14px',
            borderRadius: 6,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Reintentar
        </button>
      </div>
    );
  }
}

export default PdfActivityErrorBoundary;
